// 9o.6.e Slice 2 Commit 8-rest: Custom-window date validator.
//
// SINGLE SOURCE OF TRUTH for Free Agent Search custom-window date validation.
// Mirrors backend router rules verbatim (backend/src/routers/players_canonical.py
// L325-326 + L387-425) so client + server stay in lockstep.
//
// Returned shape: { valid, errors, throwMessage }
//   - errors: short, UI-friendly per-field messages for inline display
//             ({ start?, end?, general? })
//   - throwMessage: long-form message matching backend wording for thrown errors
//                   (apiService consumes this; preserves existing test regex)
//   - valid: derived boolean (no errors populated)
//
// Why two representations? UI wants short red-text-under-input messages; apiService
// wants long-form messages that mirror backend wording so users see consistent text
// whether client OR server raises. ONE function owns the rules; two outputs serve
// two consumers.
//
// Comparison strategy: ISO YYYY-MM-DD is lexicographically chronological, so
// string comparison ('2026-04-01' < '2026-04-02') is safe and timezone-free for
// end<start and future-date checks. Date arithmetic is used ONLY for span
// calculation, with explicit UTC midnight anchoring to avoid TZ off-by-one.

const FA_ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SPAN_DAYS = 365;

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

export function validateCustomWindow(startDate, endDate, today = todayIso()) {
  const errors = {};

  // ── Presence ──────────────────────────────────────────────────────────
  if (!startDate && !endDate) {
    errors.general = 'Start and end dates are required.';
    return {
      valid: false,
      errors,
      throwMessage:
        "dateRange='custom' requires both customStartDate and customEndDate (ISO YYYY-MM-DD).",
    };
  }
  if (!startDate) {
    errors.start = 'Start date is required.';
    return {
      valid: false,
      errors,
      throwMessage:
        "dateRange='custom' requires both customStartDate and customEndDate (ISO YYYY-MM-DD).",
    };
  }
  if (!endDate) {
    errors.end = 'End date is required.';
    return {
      valid: false,
      errors,
      throwMessage:
        "dateRange='custom' requires both customStartDate and customEndDate (ISO YYYY-MM-DD).",
    };
  }

  // ── Format ────────────────────────────────────────────────────────────
  if (!FA_ISO_DATE_RE.test(startDate)) {
    errors.start = 'Invalid date format. Use YYYY-MM-DD.';
  }
  if (!FA_ISO_DATE_RE.test(endDate)) {
    errors.end = 'Invalid date format. Use YYYY-MM-DD.';
  }
  if (errors.start || errors.end) {
    return {
      valid: false,
      errors,
      throwMessage: 'customStartDate and customEndDate must be ISO YYYY-MM-DD strings.',
    };
  }

  // ── Semantic: end >= start (lexicographic on ISO strings) ─────────────
  if (endDate < startDate) {
    errors.end = 'End date must be on or after start date.';
    return {
      valid: false,
      errors,
      throwMessage: `end_date (${endDate}) must be >= start_date (${startDate}).`,
    };
  }

  // ── Span: max 365 days (UTC midnight to avoid TZ drift) ───────────────
  const startMs = new Date(startDate + 'T00:00:00Z').getTime();
  const endMs = new Date(endDate + 'T00:00:00Z').getTime();
  const spanDays = Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24));
  if (spanDays > MAX_SPAN_DAYS) {
    errors.end = `Date range exceeds ${MAX_SPAN_DAYS} days (${spanDays} days). Narrow the window.`;
    return {
      valid: false,
      errors,
      throwMessage: `Date range span of ${spanDays} days exceeds maximum ${MAX_SPAN_DAYS} days. Use a narrower window.`,
    };
  }

  // ── Semantic: neither date in the future ──────────────────────────────
  if (startDate > today) {
    errors.start = 'Start date cannot be in the future.';
    return {
      valid: false,
      errors,
      throwMessage: `start_date (${startDate}) cannot be in the future (today: ${today}).`,
    };
  }
  if (endDate > today) {
    errors.end = 'End date cannot be in the future.';
    return {
      valid: false,
      errors,
      throwMessage: `end_date (${endDate}) cannot be in the future (today: ${today}).`,
    };
  }

  return { valid: true, errors: {}, throwMessage: null };
}

export default validateCustomWindow;
